import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const CookieBanner = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Verificar si el usuario ya ha dado su consentimiento
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const allPreferences = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    savePreferences(allPreferences);
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const minimalPreferences = {
      necessary: true,
      analytics: false,
      marketing: false
    };
    savePreferences(minimalPreferences);
    setShowBanner(false);
  };

  const handleSaveConfig = () => {
    savePreferences(preferences);
    setShowBanner(false);
    setShowConfig(false);
  };

  const savePreferences = (prefs) => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      timestamp: new Date().toISOString(),
      preferences: prefs
    }));
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center pointer-events-none">
      {/* Overlay cuando está en modo configuración */}
      {showConfig && (
        <div 
          className="absolute inset-0 bg-black/40 pointer-events-auto"
          onClick={() => setShowConfig(false)}
        />
      )}

      {/* Banner Principal */}
      <div className="relative w-full max-w-7xl mx-4 mb-4 pointer-events-auto">
        <div className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
          {!showConfig ? (
            // Vista Simple (Banner inferior)
            <div className="p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="flex-1">
                  <p className="text-gray-800 text-sm md:text-base leading-relaxed">
                    {t('cookieBanner.message')}
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <button
                    onClick={() => setShowConfig(true)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {t('cookieBanner.configure')}
                  </button>
                  <button
                    onClick={handleRejectAll}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {t('cookieBanner.reject')}
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="px-6 py-2 text-sm font-medium text-white bg-cmv-green rounded-lg hover:bg-cmv-green/90 transition-colors"
                  >
                    {t('cookieBanner.accept')}
                  </button>
                </div>
              </div>
              <div className="mt-4">
                <a
                  href="/politica-cookies"
                  className="text-sm text-cmv-green hover:underline"
                >
                  {t('cookieBanner.moreInfo')}
                </a>
              </div>
            </div>
          ) : (
            // Panel de Configuración
            <div className="p-6 md:p-8 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {t('cookieBanner.configTitle')}
                </h3>
                <button
                  onClick={() => setShowConfig(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-6">
                {/* Cookies Necesarias */}
                <div className="border-b border-gray-200 pb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold text-gray-900">
                      {t('cookieBanner.necessary.title')}
                    </h4>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-500 mr-2">
                        {t('cookieBanner.alwaysActive')}
                      </span>
                      <div className="w-12 h-6 bg-cmv-green rounded-full flex items-center justify-end px-1">
                        <div className="w-4 h-4 bg-white rounded-full" />
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    {t('cookieBanner.necessary.description')}
                  </p>
                </div>

                {/* Cookies Analíticas */}
                <div className="border-b border-gray-200 pb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold text-gray-900">
                      {t('cookieBanner.analytics.title')}
                    </h4>
                    <button
                      onClick={() => setPreferences(prev => ({
                        ...prev,
                        analytics: !prev.analytics
                      }))}
                      className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                        preferences.analytics 
                          ? 'bg-cmv-green justify-end' 
                          : 'bg-gray-300 justify-start'
                      } px-1`}
                    >
                      <div className="w-4 h-4 bg-white rounded-full" />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">
                    {t('cookieBanner.analytics.description')}
                  </p>
                </div>

                {/* Cookies de Marketing */}
                <div className="pb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold text-gray-900">
                      {t('cookieBanner.marketing.title')}
                    </h4>
                    <button
                      onClick={() => setPreferences(prev => ({
                        ...prev,
                        marketing: !prev.marketing
                      }))}
                      className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                        preferences.marketing 
                          ? 'bg-cmv-green justify-end' 
                          : 'bg-gray-300 justify-start'
                      } px-1`}
                    >
                      <div className="w-4 h-4 bg-white rounded-full" />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">
                    {t('cookieBanner.marketing.description')}
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <button
                  onClick={handleRejectAll}
                  className="flex-1 px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t('cookieBanner.reject')}
                </button>
                <button
                  onClick={handleSaveConfig}
                  className="flex-1 px-6 py-3 text-sm font-medium text-white bg-cmv-green rounded-lg hover:bg-cmv-green/90 transition-colors"
                >
                  {t('cookieBanner.saveConfig')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
